import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { faqService } from '../api';
import { Button, Input, ThemedText, RichTextEditor } from '../components';
import { FAQ_CATEGORIES, getCategoryIcon, DEFAULT_CATEGORY_ICONS } from '../constants/faqCategories';
import { renderFormIcon } from '../utils/iconUtils';
import { IconPicker } from '../components/IconPicker';
import { PageHeaderRow } from '../components/PageStyles';
import { FormLabel as Label, StyledSelect } from '../components/FormStyles';
import { ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { useEditorImageUpload } from '../hooks/useEditorImageUpload';

const stripHtmlTags = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CategorySelectRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  color: ${props => props.theme.colors.text};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const AIButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.tint + '10'};
    border-color: ${props => props.theme.colors.tint};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconPickerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint + '10'};
  }
`;

const SpinningLoader = styled(Loader2)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SectionHeader = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionDivider = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CUSTOM_CATEGORY = '__custom__';

export const CreateFaqPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPolishing, setIsPolishing] = useState(false);
  const [manualIconSet, setManualIconSet] = useState(false);
  const handleEditorImageUpload = useEditorImageUpload();

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'General',
    categoryIcon: '',
    displayOrder: 0,
    isActive: true,
    isPublic: true,
  });
  const [categorySelectValue, setCategorySelectValue] = useState('General');
  const [customCategory, setCustomCategory] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchFaq();
    }
  }, [id]);

  const fetchFaq = async () => {
    try {
      setLoading(true);
      const faq = await faqService.getFaqById(id);
      const cat = faq.category || 'General';
      const isPredefined = FAQ_CATEGORIES.some(c => c.value === cat);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: cat,
        categoryIcon: faq.categoryIcon || '',
        displayOrder: faq.displayOrder,
        isActive: faq.isActive,
        isPublic: faq.isPublic,
      });
      if (isPredefined) {
        setCategorySelectValue(cat);
        setCustomCategory('');
      } else {
        setCategorySelectValue(CUSTOM_CATEGORY);
        setCustomCategory(cat);
      }
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
      toast.error('Failed to load FAQ');
      navigate('/faqs');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    setCategorySelectValue(value);
    if (value === CUSTOM_CATEGORY) {
      setCustomCategory('');
      setFormData(prev => ({ ...prev, category: '' }));
    } else {
      setCustomCategory('');
      setFormData(prev => ({ ...prev, category: value }));
      if (!manualIconSet && DEFAULT_CATEGORY_ICONS[value]) {
        setFormData(prev => ({ ...prev, category: value, categoryIcon: DEFAULT_CATEGORY_ICONS[value] }));
      }
    }
  };

  const handleCustomCategoryChange = (value) => {
    setCustomCategory(value);
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleGenerateAnswer = async () => {
    if (!formData.question.trim()) return;
    setIsGenerating(true);
    try {
      const result = await faqService.generateAnswer(formData.question);
      if (result.success) {
        setFormData(prev => ({ ...prev, answer: result.answer }));
        toast.success('Answer generated successfully');
      } else {
        toast.error(result.errorMessage || 'Failed to generate answer');
      }
    } catch (error) {
      console.error('Failed to generate answer:', error);
      toast.error('Failed to generate answer');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePolishAnswer = async () => {
    if (!formData.question.trim() || !stripHtmlTags(formData.answer)) return;
    setIsPolishing(true);
    try {
      const result = await faqService.polishAnswer(formData.question, formData.answer);
      if (result.success) {
        setFormData(prev => ({ ...prev, answer: result.answer }));
        toast.success('Answer polished successfully');
      } else {
        toast.error(result.errorMessage || 'Failed to polish answer');
      }
    } catch (error) {
      console.error('Failed to polish answer:', error);
      toast.error('Failed to polish answer');
    } finally {
      setIsPolishing(false);
    }
  };

  const handleSubmit = async () => {
    const validation = isEditMode
      ? faqService.validateUpdateData(formData)
      : faqService.validateCreateData(formData);

    if (!validation.isValid) {
      toast.error(validation.errors.join(', '));
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        await faqService.updateFaq(id, formData);
        toast.success('FAQ updated successfully');
      } else {
        await faqService.createFaq(formData);
        toast.success('FAQ created successfully');
      }
      navigate('/faqs');
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} FAQ:`, error);
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} FAQ`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout currentPath="/faqs">
        <PageContainer>
          <ThemedText>Loading FAQ...</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/faqs">
      <PageContainer>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => navigate('/faqs')}>
              <ArrowLeft size={16} /> Back to FAQs
            </BackButton>
            <ThemedText type="title" style={{ marginTop: theme.spacing.sm }}>
              {isEditMode ? 'Edit FAQ' : 'Create New FAQ'}
            </ThemedText>
          </div>
        </PageHeaderRow>

        <FormCard theme={theme}>
          <SectionHeader theme={theme}>Content</SectionHeader>

          <FormGroup theme={theme}>
            <Label theme={theme}>Question *</Label>
            <Input
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="Enter the question"
              maxLength={500}
            />
            <div style={{ marginTop: theme.spacing.xs }}>
              <AIButton
                theme={theme}
                onClick={handleGenerateAnswer}
                disabled={!formData.question.trim() || isGenerating}
              >
                {isGenerating ? <SpinningLoader size={14} /> : <Sparkles size={14} />}
                {isGenerating ? 'Generating...' : 'Generate Answer'}
              </AIButton>
            </div>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Answer *</Label>
            <RichTextEditor
              value={formData.answer}
              onChange={(html) => setFormData(prev => ({ ...prev, answer: html }))}
              onImageUpload={handleEditorImageUpload}
              placeholder="Enter the answer..."
              variant="default"
              height={300}
            />
            <div style={{ marginTop: theme.spacing.xs }}>
              <AIButton
                theme={theme}
                onClick={handlePolishAnswer}
                disabled={!stripHtmlTags(formData.answer) || isPolishing}
              >
                {isPolishing ? <SpinningLoader size={14} /> : <Sparkles size={14} />}
                {isPolishing ? 'Polishing...' : 'Polish Answer'}
              </AIButton>
            </div>
          </FormGroup>

          <SectionDivider theme={theme} />
          <SectionHeader theme={theme}>Settings</SectionHeader>

          <FormGroup theme={theme}>
            <Label theme={theme}>Category</Label>
            <CategorySelectRow theme={theme}>
              <IconPickerButton
                theme={theme}
                type="button"
                onClick={() => setIsIconPickerOpen(true)}
                title="Choose category icon"
              >
                {formData.categoryIcon ? renderFormIcon(formData.categoryIcon, 18) : (() => {
                  const CatIcon = getCategoryIcon(formData.category || 'General');
                  return <CatIcon size={18} />;
                })()}
              </IconPickerButton>
              <StyledSelect
                theme={theme}
                value={categorySelectValue}
                onChange={(e) => handleCategoryChange(e.target.value)}
                style={{ flex: 1 }}
              >
                {FAQ_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
                <option value={CUSTOM_CATEGORY}>Custom...</option>
              </StyledSelect>
              {categorySelectValue === CUSTOM_CATEGORY && (
                <Input
                  value={customCategory}
                  onChange={(e) => handleCustomCategoryChange(e.target.value)}
                  placeholder="Enter custom category"
                  maxLength={100}
                  style={{ flex: 1 }}
                />
              )}
            </CategorySelectRow>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Display Order</Label>
            <Input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              placeholder="0"
              style={{ maxWidth: 120 }}
            />
          </FormGroup>

          <FormRow theme={theme}>
            <FormGroup theme={theme}>
              <CheckboxLabel theme={theme}>
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Active
              </CheckboxLabel>
            </FormGroup>
            <FormGroup theme={theme}>
              <CheckboxLabel theme={theme}>
                <Checkbox
                  checked={formData.isPublic}
                  onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                />
                Public (visible to all users)
              </CheckboxLabel>
            </FormGroup>
          </FormRow>

          <ButtonRow theme={theme}>
            <Button variant="secondary" onClick={() => navigate('/faqs')}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : (isEditMode ? 'Update FAQ' : 'Create FAQ')}
            </Button>
          </ButtonRow>
        </FormCard>

        <IconPicker
          isOpen={isIconPickerOpen}
          onClose={() => setIsIconPickerOpen(false)}
          onSelect={(iconName) => {
            setFormData(prev => ({ ...prev, categoryIcon: iconName }));
            setManualIconSet(true);
            setIsIconPickerOpen(false);
          }}
          selectedIcon={formData.categoryIcon || 'clipboard-list'}
        />
      </PageContainer>
    </Layout>
  );
};
